import {
  categoryAncestorPath,
  createApiClient,
  type SpaceNotifyLevel,
  type LurkedSpace,
  type PublicIdentity,
  type PublicSpace,
  type PublicSpaceChannel,
  type PublicSpaceChannelCategory,
  type PublicSpaceMessage,
} from '@adieuu/shared';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useAppConfig, usePlatformCapabilities } from '../../config';
import type {
  SpaceAlertsContext,
  SpaceChannelUnreadState,
} from '../../services/spaceSocketHandlers';
import { getSpaceCipherLink } from '../../services/spaceCipherService';
import { resolveSpaceDisplayName } from '../../pages/spaces/spaceMetadataCipher';
import {
  readableChannelRef,
  readableSpaceRef,
  viewerCanReadEncryptedChannel,
} from '../../services/spaceChannelReadability';
import { useChatSocket } from '../useChatSocket';
import { useCipherStore } from '../useCipherStore';
import { useClaimAchievement } from '../useClaimAchievement';
import { useIdentity } from '../useIdentity';
import { useInbox } from '../useInbox';
import { useSpaceNotificationSettings } from '../useSpaceNotificationSettings';
import { useNotificationDisplaySettings } from '../useNotificationDisplaySettings';
import {
  useMentionNotificationSoundPreference,
  useNotificationSoundPreference,
} from '../useNotificationSoundPreference';
import { SpacesContext } from './context';
import type { SpaceChannelMessagesState, SpacesContextValue } from './types';
import { useActiveChannelPermissions } from './useActiveChannelPermissions';
import { useActiveSpacePermissions } from './useActiveSpacePermissions';
import { useSpaceActiveSelection } from './useSpaceActiveSelection';
import { useSpaceChannelStructure } from './useSpaceChannelStructure';
import { useSpaceDataFetching } from './useSpaceDataFetching';
import { useSpaceMessageWindow } from './useSpaceMessageWindow';
import { useSpaceNotifications } from './useSpaceNotifications';
import { useSpaceParticipantProfiles } from './useSpaceParticipantProfiles';
import { useSpaceSend } from './useSpaceSend';
import { useSpacesLifecycleEffects } from './useSpacesLifecycleEffects';
import { useSpacesSocketEffects } from './useSpacesSocketEffects';
import { useSpaceUnreadSync } from './useSpaceUnreadSync';

export function SpacesProvider({ children }: { children: ReactNode }) {
  // ---------------------------------------------------------------------------
  // Provider dependencies
  // ---------------------------------------------------------------------------
  const { apiBaseUrl } = useAppConfig();
  const { status: identityStatus, identity } = useIdentity();
  const isLoggedIn = identityStatus === 'logged_in';
  const { subscribe, onStateChange } = useChatSocket();
  const api = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { notifications, audio } = usePlatformCapabilities();
  const soundPref = useNotificationSoundPreference();
  const mentionSoundPref = useMentionNotificationSoundPreference();
  const claimAchievement = useClaimAchievement();

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [spaces, setSpaces] = useState<PublicSpace[]>([]);
  const [lurkedSpacesRaw, setLurkedSpaces] = useState<LurkedSpace[]>([]);
  const [spacesLoading, setSpacesLoading] = useState(true);

  const [activeSpaceInternal, setActiveSpaceInternal] = useState<PublicSpace | null>(null);
  const [activeSpaceLoading, setActiveSpaceLoading] = useState(false);
  const [activeSpaceError, setActiveSpaceError] = useState<'not_found' | 'error' | null>(null);

  const [channels, setChannels] = useState<PublicSpaceChannel[]>([]);
  const [categories, setCategories] = useState<PublicSpaceChannelCategory[]>([]);
  const [activeChannelId, setActiveChannelIdState] = useState<string | null>(null);
  const [messagesByChannel, setMessagesByChannel] = useState<Record<string, SpaceChannelMessagesState>>({});
  const [sending, setSending] = useState(false);
  const [participantProfiles, setParticipantProfiles] = useState<Record<string, PublicIdentity>>({});
  const [unreadByChannel, setUnreadByChannel] = useState<Record<string, SpaceChannelUnreadState>>({});
  const [unreadBySpace, setUnreadBySpace] = useState<Record<string, number>>({});
  const [hasFocus, setHasFocus] = useState(
    () => typeof document !== 'undefined' && document.hasFocus(),
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const update = () => setHasFocus(document.hasFocus());
    window.addEventListener('focus', update);
    window.addEventListener('blur', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      window.removeEventListener('focus', update);
      window.removeEventListener('blur', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Refs (kept in sync each render for stable callbacks / socket wiring)
  // ---------------------------------------------------------------------------
  const socketCallbacksRef = useRef<{
    onReactionAdded?: SpacesContextValue['onSocketReactionAdded'];
    onReactionRemoved?: SpacesContextValue['onSocketReactionRemoved'];
    onPinsUpdated?: SpacesContextValue['onSocketPinsUpdated'];
  }>({});

  const { getCipherKey } = useCipherStore();
  const getCipherKeyRef = useRef(getCipherKey);
  getCipherKeyRef.current = getCipherKey;

  const spacesRef = useRef(spaces);
  const lurkedSpacesRef = useRef(lurkedSpacesRaw);
  const activeSpaceIdRef = useRef<string | null>(null);
  const activeSpaceSlugRef = useRef<string | null>(null);
  const activeSpaceLoadingRef = useRef(false);
  const channelsLengthRef = useRef(0);
  const activeChannelIdRef = useRef<string | null>(null);
  const identityIdRef = useRef<string | undefined>(identity?.id);
  const activeSpaceRoleIdsRef = useRef<string[]>([]);

  const channelsRef = useRef(channels);
  const categoriesRef = useRef(categories);
  const activeSpaceRef = useRef(activeSpaceInternal);
  channelsRef.current = channels;
  categoriesRef.current = categories;
  activeSpaceRef.current = activeSpaceInternal;
  spacesRef.current = spaces;
  lurkedSpacesRef.current = lurkedSpacesRaw;
  activeSpaceIdRef.current = activeSpaceInternal?.id ?? null;
  activeSpaceSlugRef.current = activeSpaceInternal?.slug ?? null;
  activeSpaceLoadingRef.current = activeSpaceLoading;
  channelsLengthRef.current = channels.length;
  activeChannelIdRef.current = activeChannelId;
  identityIdRef.current = identity?.id;

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------
  const {
    fetchSpaces,
    fetchLurkedSpaces,
    invalidateLurkedFetch,
    resolveSpace,
    clearActiveSpace,
    fetchChannelMessages,
    refreshChannelMessages,
    fetchMessagesAround: fetchMessagesAroundInternal,
  } = useSpaceDataFetching({
    api,
    isLoggedIn,
    setSpaces,
    setLurkedSpaces,
    setSpacesLoading,
    setActiveSpace: setActiveSpaceInternal,
    setActiveSpaceLoading,
    setActiveSpaceError,
    setChannels,
    setCategories,
    setMessagesByChannel,
  });

  const refreshSpacesRef = useRef(fetchSpaces);
  refreshSpacesRef.current = fetchSpaces;

  const refreshChannelMessagesRef = useRef(refreshChannelMessages);
  refreshChannelMessagesRef.current = refreshChannelMessages;

  // ---------------------------------------------------------------------------
  // Permissions (space + channel scope)
  // ---------------------------------------------------------------------------
  const {
    activeSpacePermissions,
    activeSpaceRoleIds,
    isActiveSpaceAdmin,
    activeSpacePermissionsLoading,
    rolePermissionPreview,
    setRolePermissionPreview,
    hasActiveSpacePermission,
    canAccessSpaceManage,
    clearActiveSpacePermissions,
    refreshActiveSpacePermissions,
  } = useActiveSpacePermissions({
    api,
    isLoggedIn,
    activeSpaceId: activeSpaceInternal?.id,
  });
  activeSpaceRoleIdsRef.current = activeSpaceRoleIds;

  // ---------------------------------------------------------------------------
  // Notification levels (live socket gating)
  // ---------------------------------------------------------------------------
  const notificationSettings = useSpaceNotificationSettings();
  const { display: notificationDisplay } = useNotificationDisplaySettings();
  const viewerRoleIdsBySpaceRef = useRef<Record<string, string[]>>({});
  viewerRoleIdsBySpaceRef.current = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const s of spaces) if (s.viewerRoleIds) map[s.id] = s.viewerRoleIds;
    return map;
  }, [spaces]);
  const resolveNotifyLevel = useCallback(
    (spaceId: string, channelId: string, categoryPath: readonly string[]): SpaceNotifyLevel => {
      let path = categoryPath;
      // Older broadcasts carry no path; the open Space's layout can supply it.
      if (path.length === 0 && spaceId === activeSpaceIdRef.current) {
        const channel = channelsRef.current.find((c) => c.id === channelId);
        path = categoryAncestorPath(channel?.categoryId, categoriesRef.current);
      }
      return notificationSettings.resolveLevel(spaceId, { channelId, categoryPath: path }).level;
    },
    [notificationSettings],
  );
  const resolveNotifyLevelRef = useRef(resolveNotifyLevel);
  resolveNotifyLevelRef.current = resolveNotifyLevel;
  const spaceAlertsRef = useRef<SpaceAlertsContext>({});
  spaceAlertsRef.current = {
    notificationDisplay: notificationDisplay.spaces,
    resolveAnnouncements: notificationSettings.resolveAnnouncements,
    // Lurkers still receive Space broadcasts; only members get change alerts.
    resolveSpaceChanges: (spaceId) =>
      spacesRef.current.some((s) => s.id === spaceId) &&
      notificationSettings.resolveToggle(spaceId, 'spaceChanges'),
    spaceNameFor: (spaceId) => {
      const space = spacesRef.current.find((s) => s.id === spaceId);
      if (!space) return undefined;
      const cipherId = getSpaceCipherLink(space.id);
      const cipher = cipherId ? getCipherKeyRef.current(cipherId) : null;
      return resolveSpaceDisplayName(space, cipher, {
        encryptedSpace: t('spaces.encryptedSpacePlaceholder'),
      });
    },
    openAnnouncement: (spaceId, announcementId) => {
      const slug = spacesRef.current.find((s) => s.id === spaceId)?.slug;
      if (!slug) return;
      navigate(
        `/s/${encodeURIComponent(slug)}?tab=announcements&announcement=${encodeURIComponent(announcementId)}`,
      );
    },
    openSpace: (spaceId) => {
      const slug = spacesRef.current.find((s) => s.id === spaceId)?.slug;
      if (slug) navigate(`/s/${encodeURIComponent(slug)}`);
    },
  };

  const {
    activeChannelPermissions,
    activeChannelPermissionsLoading,
    hasActiveChannelPermission,
  } = useActiveChannelPermissions({
    api,
    isLoggedIn,
    spaceId: activeSpaceInternal?.id,
    channelId: activeChannelId,
    channels,
    categories,
  });

  // ---------------------------------------------------------------------------
  // Participant profiles
  // ---------------------------------------------------------------------------
  const {
    resolveProfiles: resolveProfilesPublic,
    resolveSigningKeysPublic,
    signingKeys,
    pqSigningKeys,
  } = useSpaceParticipantProfiles({
    api,
    setParticipantProfiles,
    messagesByChannel,
  });

  // ---------------------------------------------------------------------------
  // Channel/category structure
  // ---------------------------------------------------------------------------
  const { addChannelLocally, addCategoryLocally, removeCategoryLocally, applyChannelLayout } =
    useSpaceChannelStructure({
      api,
      channels,
      categories,
      setChannels,
      setCategories,
      activeSpaceIdRef,
    });

  // ---------------------------------------------------------------------------
  // Active selection + unread
  // ---------------------------------------------------------------------------
  // Guest-safe: public shell mounts SpacesProvider without InboxProvider, so
  // useInbox returns no-ops there.
  const { markItemsReadForChannel, setAutoReadChannel, refreshUnseen } = useInbox();
  const handleChannelRead = useCallback(
    (channelId: string, uptoMessageId: string) => {
      markItemsReadForChannel(channelId, uptoMessageId);
    },
    [markItemsReadForChannel],
  );
  const { setActiveSpace, setActiveChannel, clearChannelUnread, markSpaceRead } =
    useSpaceActiveSelection({
      activeSpaceIdRef,
      activeSpaceSlugRef,
      activeSpaceLoadingRef,
      channelsLengthRef,
      activeSpaceId: activeSpaceInternal?.id ?? null,
      activeChannelId,
      messagesByChannel,
      resolveSpace,
      clearActiveSpace,
      fetchChannelMessages,
      setActiveChannelId: setActiveChannelIdState,
      setUnreadBySpace,
      setUnreadByChannel,
      clearActiveSpacePermissions,
      onChannelActivated: markItemsReadForChannel,
    });

  const removeSpaceLocally = useCallback(
    (spaceId: string) => {
      setSpaces((prev) => prev.filter((s) => s.id !== spaceId));
      setUnreadBySpace((prev) => {
        if (!prev[spaceId]) return prev;
        const { [spaceId]: _, ...rest } = prev;
        return rest;
      });
      if (activeSpaceIdRef.current === spaceId) {
        clearActiveSpace();
        setActiveChannelIdState(null);
        clearActiveSpacePermissions();
      }
    },
    [clearActiveSpace, clearActiveSpacePermissions],
  );

  // ---------------------------------------------------------------------------
  // Messaging (send + window pagination)
  // ---------------------------------------------------------------------------
  const { sendMessage } = useSpaceSend({
    api,
    activeSpaceIdRef,
    activeChannelIdRef,
    setSending,
    setMessagesByChannel,
    showError: (msg: string) => toast.error(msg),
  });

  const {
    loadOlderMessages,
    loadNewerMessages,
    jumpToLatestMessages,
    fetchMessagesAround,
    trimActiveChannelBuffer,
  } = useSpaceMessageWindow({
    activeSpaceIdRef,
    activeChannelIdRef,
    messagesByChannel,
    fetchChannelMessages,
    fetchMessagesAroundInternal,
    setMessagesByChannel,
  });

  // ---------------------------------------------------------------------------
  // Notifications
  // ---------------------------------------------------------------------------
  const { fireNotification } = useSpaceNotifications({
    toast,
    soundPref,
    mentionSoundPref,
    notifications,
    audio,
    claimAchievement,
    navigate,
    activeSpaceSlug: activeSpaceInternal?.slug,
    spacesRef,
  });

  // ---------------------------------------------------------------------------
  // WebSocket wiring (refs keep socket handlers pointed at latest closures)
  // ---------------------------------------------------------------------------
  const registerSocketCallbacks = useCallback(
    (callbacks: {
      onReactionAdded?: SpacesContextValue['onSocketReactionAdded'];
      onReactionRemoved?: SpacesContextValue['onSocketReactionRemoved'];
      onPinsUpdated?: SpacesContextValue['onSocketPinsUpdated'];
    }) => {
      socketCallbacksRef.current = callbacks;
    },
    [],
  );

  const fireNotificationRef = useRef(fireNotification);
  fireNotificationRef.current = fireNotification;

  const tRef = useRef(t);
  tRef.current = t;

  const canReadEncryptedMessageRef = useRef<(msg: PublicSpaceMessage) => boolean>(() => false);
  canReadEncryptedMessageRef.current = (msg) => {
    const channel = channelsRef.current.find((candidate) => candidate.id === msg.channelId) ?? null;
    const matchingChannel =
      channel && channel.spaceId === msg.spaceId ? readableChannelRef(channel) : null;
    const space =
      (activeSpaceRef.current?.id === msg.spaceId ? activeSpaceRef.current : null) ??
      spacesRef.current.find((candidate) => candidate.id === msg.spaceId) ??
      null;
    return viewerCanReadEncryptedChannel({
      spaceId: msg.spaceId,
      channelId: msg.channelId,
      space: readableSpaceRef(space),
      channel: matchingChannel,
      getCipherKey: (localId) => getCipherKeyRef.current(localId),
    });
  };

  const channelNamesRef = useRef<Record<string, string>>({});
  channelNamesRef.current = useMemo(
    () => Object.fromEntries(channels.map((ch) => [ch.id, ch.name])),
    [channels],
  );

  const participantProfilesRef = useRef<Record<string, PublicIdentity>>({});
  participantProfilesRef.current = participantProfiles;

  const activeChannelMessagesRef = useRef<PublicSpaceMessage[]>([]);
  activeChannelMessagesRef.current = activeChannelId
    ? messagesByChannel[activeChannelId]?.messages ?? []
    : [];

  const removeSpaceLocallyRef = useRef(removeSpaceLocally);
  removeSpaceLocallyRef.current = removeSpaceLocally;

  const handleSpaceDeleted = useCallback(
    (spaceId: string) => {
      const wasActive = activeSpaceIdRef.current === spaceId;
      removeSpaceLocallyRef.current(spaceId);
      if (wasActive) {
        navigate('/search?tab=spaces');
      }
    },
    [navigate],
  );

  // Durable unread + mention reconciliation: hydrate badges from the server on
  // load / reconnect and persist the read cursor while viewing a channel.
  useSpaceUnreadSync({
    api,
    isLoggedIn,
    spaces,
    onStateChange,
    activeSpaceId: activeSpaceInternal?.id ?? null,
    activeChannelId,
    activeChannelNewestMessageId: activeChannelId
      ? messagesByChannel[activeChannelId]?.messages?.[0]?.id
      : undefined,
    hasFocus,
    setUnreadByChannel,
    setUnreadBySpace,
    onChannelRead: handleChannelRead,
    onChannelReadPersisted: refreshUnseen,
  });

  useEffect(() => {
    setAutoReadChannel(hasFocus && activeChannelId ? activeChannelId : null);
  }, [hasFocus, activeChannelId, setAutoReadChannel]);

  useSpacesLifecycleEffects({ fetchSpaces, fetchLurkedSpaces });

  const lurkedSpaces = useMemo(
    () => lurkedSpacesRaw.filter((space) => !spaces.some((member) => member.id === space.id)),
    [lurkedSpacesRaw, spaces],
  );

  const isSpaceLurked = useCallback(
    (spaceId: string) => lurkedSpaces.some((space) => space.id === spaceId),
    [lurkedSpaces],
  );

  const lurkSpace = useCallback(
    async (space: PublicSpace) => {
      invalidateLurkedFetch();
      const already = lurkedSpacesRef.current.some((s) => s.id === space.id);
      if (!already) {
        setLurkedSpaces((prev) => [
          ...prev,
          { ...space, lurkingSince: new Date().toISOString() },
        ]);
      }
      try {
        const res = await api.spaces.updatePreferences(space.id, { lurking: true });
        invalidateLurkedFetch();
        if (!res.success) {
          if (!already) setLurkedSpaces((prev) => prev.filter((s) => s.id !== space.id));
          return false;
        }
        const since = res.data?.lurkingSince;
        if (since) {
          setLurkedSpaces((prev) =>
            prev.map((s) => (s.id === space.id ? { ...s, lurkingSince: since } : s)),
          );
        }
        return true;
      } catch {
        invalidateLurkedFetch();
        if (!already) setLurkedSpaces((prev) => prev.filter((s) => s.id !== space.id));
        return false;
      }
    },
    [api, invalidateLurkedFetch],
  );

  const stopLurking = useCallback(
    async (spaceId: string) => {
      invalidateLurkedFetch();
      const snapshot = lurkedSpacesRef.current;
      setLurkedSpaces((prev) => prev.filter((s) => s.id !== spaceId));
      try {
        const res = await api.spaces.updatePreferences(spaceId, { lurking: false });
        invalidateLurkedFetch();
        if (!res.success) {
          setLurkedSpaces(snapshot);
          return false;
        }
        return true;
      } catch {
        invalidateLurkedFetch();
        setLurkedSpaces(snapshot);
        return false;
      }
    },
    [api, invalidateLurkedFetch],
  );

  const handleViewerMemberUpdated = useCallback(
    (spaceId: string) => {
      if (spaceId === activeSpaceIdRef.current) refreshActiveSpacePermissions();
    },
    [refreshActiveSpacePermissions],
  );

  /** Apply a Space returned by a successful PATCH before its `space_updated` arrives. */
  const applySpaceUpdate = useCallback((space: PublicSpace) => {
    setSpaces((prev) =>
      prev.map((s) =>
        s.id === space.id
          ? { ...space, ...(s.viewerRoleIds ? { viewerRoleIds: s.viewerRoleIds } : {}) }
          : s,
      ),
    );
    setActiveSpaceInternal((prev) =>
      prev && prev.id === space.id
        ? {
            ...space,
            ...(prev.membershipStatus ? { membershipStatus: prev.membershipStatus } : {}),
            ...(prev.banExpiresAt !== undefined ? { banExpiresAt: prev.banExpiresAt } : {}),
          }
        : prev,
    );
  }, []);

  useSpacesSocketEffects({
    isLoggedIn,
    subscribe,
    onStateChange,
    setSpaces,
    setChannels,
    setCategories,
    setMessagesByChannel,
    activeSpaceIdRef,
    activeChannelIdRef,
    identityIdRef,
    activeSpaceRoleIdsRef,
    viewerRoleIdsBySpaceRef,
    resolveNotifyLevelRef,
    spaceAlertsRef,
    refreshSpacesRef,
    refreshChannelMessagesRef,
    socketCallbacksRef,
    setUnreadByChannel,
    setUnreadBySpace,
    fireNotificationRef,
    channelNamesRef,
    canReadEncryptedMessageRef,
    participantProfilesRef,
    activeChannelMessagesRef,
    onSpaceDeleted: handleSpaceDeleted,
    tRef,
    setActiveSpace: setActiveSpaceInternal,
    onViewerMemberUpdated: handleViewerMemberUpdated,
  });

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------
  const activeChannelState = activeChannelId ? messagesByChannel[activeChannelId] : undefined;

  // While listMine is in flight, treat as member so the join CTA / interstitial
  // path does not flash for Spaces the user already belongs to.
  const isActiveSpaceMember = useMemo(() => {
    if (!activeSpaceInternal) return false;
    if (spaces.some((s) => s.id === activeSpaceInternal.id)) return true;
    return spacesLoading;
  }, [activeSpaceInternal, spaces, spacesLoading]);

  const value = useMemo<SpacesContextValue>(
    () => ({
      spaces,
      lurkedSpaces,
      spacesLoading,
      isSpaceLurked,
      lurkSpace,
      stopLurking,
      activeSpace: activeSpaceInternal,
      activeSpaceLoading,
      activeSpaceError,
      isActiveSpaceMember,
      isGuest: !isLoggedIn,
      activeSpacePermissions,
      isActiveSpaceAdmin,
      hasActiveSpacePermission,
      canAccessSpaceManage,
      activeSpacePermissionsLoading,
      refreshActiveSpacePermissions,
      applySpaceUpdate,
      rolePermissionPreview,
      setRolePermissionPreview,
      channels,
      categories,
      activeSpaceRoleIds,
      activeChannelPermissions,
      activeChannelPermissionsLoading,
      hasActiveChannelPermission,
      addChannelLocally,
      addCategoryLocally,
      removeCategoryLocally,
      applyChannelLayout,
      activeChannelId,
      activeMessages: activeChannelState?.messages ?? [],
      activeMessagesLoading: activeChannelState?.loading ?? false,
      activeMessagesLoadingOverlay: activeChannelState?.loadingOverlay ?? false,
      activeMessagesOlderCursor: activeChannelState?.olderCursor ?? null,
      activeMessagesHasNewerPages: activeChannelState?.hasNewerPages ?? false,
      sending,
      participantProfiles,
      signingKeys,
      pqSigningKeys,
      unreadByChannel,
      unreadBySpace,
      resolveProfiles: resolveProfilesPublic,
      resolveSigningKeys: resolveSigningKeysPublic,
      setActiveSpace,
      setActiveChannel,
      sendMessage,
      loadOlderMessages,
      loadNewerMessages,
      jumpToLatestMessages,
      fetchMessagesAround,
      trimActiveChannelBuffer,
      refresh: fetchSpaces,
      removeSpaceLocally,
      clearChannelUnread,
      markSpaceRead,
      registerSocketCallbacks,
    }),
    [
      spaces,
      lurkedSpaces,
      spacesLoading,
      isSpaceLurked,
      lurkSpace,
      stopLurking,
      activeSpaceInternal,
      activeSpaceLoading,
      activeSpaceError,
      isActiveSpaceMember,
      isLoggedIn,
      activeSpacePermissions,
      isActiveSpaceAdmin,
      hasActiveSpacePermission,
      canAccessSpaceManage,
      activeSpacePermissionsLoading,
      refreshActiveSpacePermissions,
      applySpaceUpdate,
      rolePermissionPreview,
      setRolePermissionPreview,
      channels,
      categories,
      activeSpaceRoleIds,
      activeChannelPermissions,
      activeChannelPermissionsLoading,
      hasActiveChannelPermission,
      addChannelLocally,
      addCategoryLocally,
      removeCategoryLocally,
      applyChannelLayout,
      activeChannelId,
      activeChannelState,
      sending,
      participantProfiles,
      signingKeys,
      pqSigningKeys,
      unreadByChannel,
      unreadBySpace,
      resolveProfilesPublic,
      resolveSigningKeysPublic,
      setActiveSpace,
      setActiveChannel,
      sendMessage,
      loadOlderMessages,
      loadNewerMessages,
      jumpToLatestMessages,
      fetchMessagesAround,
      trimActiveChannelBuffer,
      removeSpaceLocally,
      fetchSpaces,
      clearChannelUnread,
      markSpaceRead,
      registerSocketCallbacks,
    ],
  );

  return <SpacesContext.Provider value={value}>{children}</SpacesContext.Provider>;
}
